import { useEffect, useRef } from "react";
import useTableCtx from "../tableContext/useTableCtx";
import TableFilterSelect from "./TableFilterSelect";

function FilterTable({
  value,
  label,
  withFilter,
  filterDefaultIdx,
}: {
  value: string;
  label?: string;
  withFilter: {
    value: string | number;
    label: string | number;
  }[];
  filterDefaultIdx?: number;
}) {
  const { handleTableFilter, tableFilter, tableSearch, setTableSearch } =
    useTableCtx()!;
  const initializedField = useRef<string>();

  const tableFilterValue = tableFilter?.[value];
  // Async dropdowns may not have been identified as filters when URL values
  // were restored. Preserve that value when moving it out of text searches.
  const restoredSearchValue = tableSearch?.[value];
  const existingValue = tableFilterValue ?? restoredSearchValue;

  const filterDefaultIdxValue = filterDefaultIdx ?? 0;
  const defaultFilterValue =
    withFilter[filterDefaultIdxValue]?.value ?? withFilter[0]?.value;
  const selectedValue = existingValue ?? defaultFilterValue ?? null;
  const hasOptions = withFilter.length > 0;
  const hasEmptyOption = withFilter.some((option) => option.value === "");

  useEffect(() => {
    if (!value || !hasOptions || initializedField.current === value) return;
    initializedField.current = value;

    // Initialize once so later deliberate empty-option selections are kept.
    const needsDefault =
      existingValue == null ||
      (existingValue === "" &&
        (filterDefaultIdx !== undefined || !hasEmptyOption));

    if (needsDefault && defaultFilterValue !== undefined) {
      handleTableFilter(value, defaultFilterValue, { resetPage: false });
    } else if (tableFilterValue == null && existingValue != null) {
      handleTableFilter(value, existingValue, { resetPage: false });
    }

    if (restoredSearchValue !== undefined) {
      setTableSearch((previous: Record<string, string | number> | null) => {
        if (!previous || !(value in previous)) return previous;
        const next = { ...previous };
        delete next[value];
        return next;
      });
    }
  }, [
    defaultFilterValue,
    existingValue,
    filterDefaultIdx,
    handleTableFilter,
    hasEmptyOption,
    hasOptions,
    restoredSearchValue,
    setTableSearch,
    tableFilterValue,
    value,
  ]);

  return (
    <div className="w-full min-w-0">
      {selectedValue !== null && (
        <TableFilterSelect
          name={value}
          label={`Filtreaza ${label ?? value}`}
          value={selectedValue}
          options={withFilter}
          fallbackOptionIndex={filterDefaultIdx}
          onChange={(nextValue) => handleTableFilter(value, nextValue)}
        />
      )}
    </div>
  );
}

export default FilterTable;
